import { useExtractDocs } from "context/ExtractDocsContext";
import { EXTRACT_OCR_STATUS } from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { isFileHISStateful } from "core/model/files/utils";
import { FileState } from "core/types";
import { Banner } from "ds/ui";
import { ExtractServiceUnavailableBanner } from "dsl/ecosystems/PatientFiles/ExtractServiceUnavailableBanner";
import { CheckCircle2Icon, SparklesIcon } from "lucide-react";
import { useTranslations } from "translations";

export const FileDetailExtractCard = ({
  file,
  fileName,
}: {
  file: FileState;
  fileName: string;
}) => {
  const translations = useTranslations();
  const { extractOcr, extractOcrStatus, utils } = useExtractDocs();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();

  if (!isDocsAndExtractActive && !isDischargeLetterActive) {
    return null;
  }

  if (extractOcrStatus.error) {
    return <ExtractServiceUnavailableBanner />;
  }

  const document = utils.getExtractedDocument(file);

  switch (document?.status) {
    case EXTRACT_OCR_STATUS.PROCESSING:
      return (
        <Banner
          color="primary"
          heading={
            translations.extractDocs.extractBanner.loadingStateBannerExtraction
          }
          isLoading
          message={
            translations.extractDocs.extractBanner
              .loadingStateBannerExtractionDescription
          }
        />
      );
    case EXTRACT_OCR_STATUS.EXTRACTED:
      return (
        <Banner
          color="success"
          heading={
            translations.extractDocs.filesTabChanges.detailViewModal
              .extractedBannerTitle
          }
          icon={CheckCircle2Icon}
          message={
            <div className="flex flex-col gap-1">
              <p>
                {
                  translations.extractDocs.filesTabChanges.detailViewModal
                    .extractedBannerDescriptionOne
                }
              </p>
              <p className="font-semibold">
                {
                  translations.extractDocs.filesTabChanges.detailViewModal
                    .extractedBannerDescriptionTwo
                }
              </p>
            </div>
          }
          withClose
        />
      );
    case EXTRACT_OCR_STATUS.FAILED:
      return (
        <Banner
          actions={[
            {
              id: "extract-file-detail-view-retry",
              isDisabled: isFileHISStateful(file),
              isLoading: utils.isFileExtractionProcessing(file),
              label: isFileHISStateful(file)
                ? translations.extractDocs.filesTabChanges
                    .buttonWaitingExtraction
                : translations.extractDocs.filesTabChanges.detailViewModal
                    .buttonTryAgain,
              onPress: () => {
                extractOcr.handleDownloadAndRunOcr({ files: [file] });
              },
              trackEventProps: {
                file_id: String(file.id),
                source: "file-detail-view",
              },
            },
          ]}
          color="danger"
          heading={translations.extractDocs.filesTabChanges.errorExtractBannerTitle(
            {
              filename: fileName,
            },
          )}
          message={
            translations.extractDocs.filesTabChanges
              .errorExtractBannerDescription
          }
          withClose
        />
      );
    default:
      return (
        <Banner
          actions={[
            {
              id: "extract-file-detail-view",
              isDisabled: isFileHISStateful(file),
              isLoading: utils.isFileExtractionProcessing(file),
              label: isFileHISStateful(file)
                ? translations.extractDocs.filesTabChanges
                    .buttonWaitingExtraction
                : translations.extractDocs.filesTabChanges.detailViewModal
                    .buttonExtract,
              onPress: () => {
                extractOcr.handleDownloadAndRunOcr({ files: [file] });
              },
              trackEventProps: {
                file_id: String(file.id),
                source: "file-detail-view",
              },
            },
          ]}
          color="primary"
          heading={
            translations.extractDocs.filesTabChanges.detailViewModal.titleBanner
          }
          icon={SparklesIcon}
          message={
            <div className="flex flex-col gap-2">
              <p>
                {
                  translations.extractDocs.filesTabChanges.detailViewModal
                    .descriptionParagraphOne
                }
              </p>
              <p className="font-semibold">
                {
                  translations.extractDocs.filesTabChanges.detailViewModal
                    .descriptionParagraphTwo
                }
              </p>
            </div>
          }
        />
      );
  }
};
